import { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { LocationCreateForm } from '@/components/LocationCreateForm';
import { useLocations, useLocationCount } from '@/stores/locations';
import { useOrg } from '@/stores/identity';
import { supabase } from '@/lib/supabase';
import { BrandedLayout } from '@/components/BrandedLayout';

/**
 * Onboarding: add your first pharmacy.
 *
 * The Home page redirects here when the org has zero locations. This page
 * itself redirects back to / once the count goes above zero (so refreshing
 * after creation lands you on the dashboard).
 */
export default function FirstLocation() {
  const navigate = useNavigate();
  const org = useOrg();
  const load = useLocations((s) => s.load);
  const status = useLocations((s) => s.status);
  const count = useLocationCount();

  useEffect(() => {
    if (status === 'idle') void load();
  }, [status, load]);

  useEffect(() => {
    if (status === 'loaded' && count > 0) {
      navigate('/', { replace: true });
    }
  }, [status, count, navigate]);

  async function handleSignOut() {
    await supabase.auth.signOut();
  }

  if (status === 'loading' || status === 'idle') {
    return (
      <BrandedLayout homeHref="/">
        <div className="container mx-auto py-16 px-4 text-center text-sm text-muted-foreground">
          Loading...
        </div>
      </BrandedLayout>
    );
  }

  const topRight = (
    <div className="flex items-center gap-3">
      <span className="hidden sm:inline-block rounded-full px-2 py-0.5 text-xs font-medium"
        style={{
          backgroundColor: 'hsl(var(--brand-navy) / 0.10)',
          color: 'hsl(var(--brand-navy))',
        }}
      >
        {org?.display_name ?? '...'}
      </span>
      <button
        type="button"
        onClick={handleSignOut}
        className="rounded-md border border-border px-3 py-1.5 text-sm hover:bg-muted/40 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
      >
        Sign out
      </button>
    </div>
  );

  return (
    <BrandedLayout homeHref="/" topRight={topRight}>
      <div className="container mx-auto py-10 px-4">
        <div className="mx-auto w-full max-w-xl space-y-6">
          <div className="space-y-1 text-center">
            <p className="text-sm text-muted-foreground">Welcome to PharmaSync</p>
            <h2 className="text-2xl font-semibold tracking-tight">
              Add your first pharmacy
            </h2>
            <p className="text-sm text-muted-foreground max-w-md mx-auto pt-2">
              You created an organization. Now add the first pharmacy under it
              as a <em>location</em>. Schedules, staff, and time-off all
              attach to a location.
            </p>
          </div>

          <div className="rounded-lg border border-border bg-card p-6">
            <LocationCreateForm
              withIntro={false}
              submitLabel="Add pharmacy"
              onCreated={() => navigate('/', { replace: true })}
            />
          </div>
        </div>
      </div>
    </BrandedLayout>
  );
}
